import { type Ref } from "vue";
import type {
  DeliveryAreaSectionListInput,
  ListDeliveryAreaSectionAssignedQuery,
} from "@/generated/graphql";
import { ListDeliveryAreaSectionAssignedDocument } from "@/generated/graphql";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { localStorageGetCustomerCode } from "@/common/localStorageUtil";
import { saveAs } from "file-saver";
import Encoding from "encoding-japanese";
import type { DeliveryAreaInput, AreaSelectValue, ShopSelectValue, ChainSelectValue, AssignedSection } from "../types";

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseAreaListDownloadParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  displayDialogDownloadSectionAssigned: Ref<boolean>;
  areaSelectValuesSectionAssigned: Ref<AreaSelectValue>;
  shopSelectValues: Ref<ShopSelectValue>;
  chainSelectValues: Ref<ChainSelectValue>;
  deliveryAreaInput: DeliveryAreaInput;
  listAreaSectionAssigned: Ref<AssignedSection>;
  searchConditionZipCodeSectionAssigned: Ref<string | null>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useAreaListDownload = ({
  errdialog,
  errMessage,
  overlay,
  displayDialogDownloadSectionAssigned,
  areaSelectValuesSectionAssigned,
  shopSelectValues,
  chainSelectValues,
  deliveryAreaInput,
  listAreaSectionAssigned,
  searchConditionZipCodeSectionAssigned,
}: UseAreaListDownloadParams) => {
  // 配達区画ダウンロード 確認 @Click
  const onClickDownloadAssignedSection = async (): Promise<void> => {
    // 配達区画一覧が無い場合は処理しない
    if (listAreaSectionAssigned.value.length === 0) {
      return;
    }
    displayDialogDownloadSectionAssigned.value = true;
  };

  // 配達区画ダウンロード 実行
  const onClickDownloadAssignedComplete = async (): Promise<void> => {
    // ロード表示
    overlay.value = true;

    const listInput: DeliveryAreaSectionListInput = {
      area_code:
        areaSelectValuesSectionAssigned.value &&
        areaSelectValuesSectionAssigned.value.area_code !== ""
          ? areaSelectValuesSectionAssigned.value.area_code
          : "",
      chain_code: deliveryAreaInput.chain_code,
      customer_code: localStorageGetCustomerCode(),
      shop_code: shopSelectValues.value.shop_code,
      zip_code: searchConditionZipCodeSectionAssigned.value || undefined,
      active_only: true, // 廃止された住所は含めない
    };

    let result: ApolloQueryResult<ListDeliveryAreaSectionAssignedQuery>;
    try {
      // graphql接続
      const apolloClient = await generateApolloClient();

      // 登録配達区画一覧取得
      result = await apolloClient.query<ListDeliveryAreaSectionAssignedQuery>({
        query: ListDeliveryAreaSectionAssignedDocument,
        variables: { input: listInput },
        fetchPolicy: "no-cache",
      });

      if (
        result.data.listDeliveryAreaSectionAssigned === undefined ||
        result.data.listDeliveryAreaSectionAssigned === null
      ) {
        // 登録配達区画一覧取得 エラー
        console.error(
          "result.data.listDeliveryAreaSectionAssigned is undefined or null"
        );
        return;
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "不明なエラーが発生しました。";
      return;
    }

    // 画面遷移
    if (result) {
      // CSV出力
      let csvData = "";
      // ヘッダー
      csvData +=
        "チェーン名, 店舗名, 店舗コード, エリア名称, エリアコード, 郵便番号, 住所, 区画コード\r\n";
      // データ
      result.data.listDeliveryAreaSectionAssigned.forEach((value) => {
        csvData +=
          [
            chainSelectValues.value?.name || "",
            shopSelectValues.value.name,
            shopSelectValues.value.shop_code,
            areaSelectValuesSectionAssigned.value.area_name,
            areaSelectValuesSectionAssigned.value.area_code,
            value?.zip_code || "",
            value?.delivery_area_section || "",
            value?.lot_code || "",
          ].join(",") + "\r\n";
      });
      // Shift-JISにエンコード
      const unicodeArray = Encoding.stringToCode(csvData);
      const sjisArray = Encoding.convert(unicodeArray, {
        to: "SJIS",
        from: "UNICODE",
      });
      const uint8Array = new Uint8Array(sjisArray);

      // ダウンロードファイル作成
      const blob = new Blob([uint8Array], {
        type: "text/csv",
      });
      // ダウンロード
      saveAs(blob, "delivery_area_section.csv");
    } else {
      errMessage.value = "配達区画のダウンロードに失敗しました。";
      errdialog.value = true;
    }
    // 画面を閉じる
    displayDialogDownloadSectionAssigned.value = false;
    overlay.value = false;
  };

  return {
    onClickDownloadAssignedSection,
    onClickDownloadAssignedComplete,
  };
};
